import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import {
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import DashboardLayout from '../../../../layouts/dashboardlayout';
import TreatmentForm from '../../../../components/hospital/forms/treatment';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTreatmentRecords,
  deleteTreatmentRecord,
  updateTreatmentRecord,
  createTreatmentRecord
} from '../../../../redux/middleware/user/hospital/treatmentrecords';
import { showToast } from '../../../../utils/config/helpers';
import { toast } from 'react-toastify';

const TreatmentHistoryPage = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);

  const { records, loading, error } = useSelector(
    (state) => state.hospitalTreatmentRecords
  );

  useEffect(() => {
    dispatch(fetchTreatmentRecords());
  }, [dispatch]); // Only depend on dispatch

  const handleAddTreatment = () => {
    setEditingTreatment(null);
    setShowForm(true);
  };

  const handleEditTreatment = (treatment) => {
    setEditingTreatment(treatment);
    setShowForm(true);
  };

  const handleDeleteTreatment = (id) => {
    // Use the Redux action to delete from backend
    dispatch(deleteTreatmentRecord(id))
      .then(() => {
        showToast(toast, 'success', 'Treatment record deleted successfully');
      })
      .catch((err) => {
        showToast(toast, 'error', 'Failed to delete treatment record');
      });
  };

  const handleFormSubmit = (treatmentData) => {
    if (editingTreatment) {
      dispatch(updateTreatmentRecord(editingTreatment.id, treatmentData))
        .then(() => {
          showToast(toast, 'success', 'Treatment record updated successfully');
          setShowForm(false);
          setEditingTreatment(null);
        })
        .catch((err) => {
          showToast(toast, 'error', 'Failed to update treatment record');
        });
    } else {
      dispatch(createTreatmentRecord(treatmentData))
        .then(() => {
          showToast(toast, 'success', 'Treatment record created successfully');
          setShowForm(false);
        })
        .catch((err) => {
          showToast(toast, 'error', 'Failed to create treatment record');
        });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTreatment(null);
  };
  // Filter treatments based on the selected tab
  const filteredTreatments =
		tabValue === 0
		  ? records
		  : tabValue === 1
		    ? records.filter((t) => t.isHospitalCreated)
		    : records.filter((t) => !t.isHospitalCreated);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={css(styles.container)}
    >
      <Typography variant='h4' className={css(styles.title)}>
        Treatment History
      </Typography>
      <Grid item xs={10}>
        {!showForm ? (
          <>
            <div className={css(styles.headerSection)}>
              <Typography variant='body1' className={css(styles.description)}>
                View and manage your treatment history.
              </Typography>
              <Button
                variant='contained'
                color='primary'
                startIcon={<Add />}
                onClick={handleAddTreatment}
                className={css(styles.addButton)}
              >
                Add Treatment
              </Button>
            </div>

            <Divider className={css(styles.divider)} />

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              className={css(styles.tabs)}
            >
              <Tab label='All Treatments' />
              <Tab label='My Records' />
              <Tab label='Provider Records' />
            </Tabs>

            {loading ? (
              <div className={css(styles.loadingContainer)}>
                <CircularProgress color='primary' />
                <Typography variant='body2'>
                  Loading your treatment history...
                </Typography>
              </div>
            ) : filteredTreatments.length === 0 ? (
              <Alert severity='info' className={css(styles.alert)}>
                No treatment records found in this category.
              </Alert>
            ) : (
              <div className={css(styles.treatmentsContainer)}>
                {filteredTreatments.map((treatment) => (
                  <Paper
                    key={treatment.id}
                    elevation={1}
                    className={css(
                      styles.treatmentCard,
                      // Since we don't have isPatientCreated, assume all are hospital created
                      // or use another condition to determine this
                      styles.hospitalCreatedCard
                    )}
                  >
                    <div className={css(styles.treatmentHeader)}>
                      <Typography
                        variant='h6'
                        className={css(styles.conditionTitle)}
                      >
                        {/* Show medication as the title if no condition field exists */}
                        {treatment.medication_prescribed}
                      </Typography>
                      {/* Allow editing for all records for now */}
                      <div className={css(styles.actionButtons)}>
                        <IconButton
                            size='small'
                            color='primary'
                            onClick={() => handleEditTreatment(treatment)}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleDeleteTreatment(treatment.id)}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                      </div>
                    </div>
                    <Typography
                      variant='body2'
                      className={css(
                        styles.treatmentDate,
                        styles.treatmentTextColor
                      )}
                    >
                      Date: {formatDate(treatment.date_of_treatment)}
                    </Typography>
                    <Typography
                      variant='body2'
                      className={css(
                        styles.treatmentProvider,
                        styles.treatmentTextColor
                      )}
                    >
                      Doctor: {treatment.doctor_name || 'Not specified'}
                    </Typography>
                    <Typography
                      variant='body2'
                      className={css(
                        styles.medicationsLabel,
                        styles.treatmentTextColor
                      )}
                    >
                      Medications:
                    </Typography>
                    {/* Medication is now a string, not an array */}
                    <Typography
                      variant='body2'
                      className={css(styles.treatmentTextColor)}
                    >
                      {treatment.medication_prescribed}
                    </Typography>

                    {treatment.notes && (
                      <Typography
                        variant='body2'
                        className={css(
                            styles.treatmentNotes,
                            styles.treatmentTextColor
                          )}
                      >
                          Notes: {treatment.notes}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </div>
            )}
          </>
        ) : (
          <TreatmentForm
            initialData={editingTreatment}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}
      </Grid>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: '1.5rem',
    maxWidth: '1200px'
    // margin: '0 auto'
  },
  title: {
    color: '#1976d2',
    marginBottom: '1.5rem',
    fontWeight: 'bold',
    fontSize: '1.8rem',
    '@media (max-width: 600px)': {
      fontSize: '1.5rem'
    }
  },
  contentPaper: {
    padding: '1.5rem',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    // boxShadow: 'none', // Remove the default shadow
    // margin: '0', // Ensure no margin is applied
    width: '100%' // Ensure it takes full width of the grid item
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '1rem',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1rem',
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '1rem'
    }
  },
  description: {
    color: '#555',
    flex: 1
  },
  addButton: {
    backgroundColor: '#2783F7',
    '&:hover': {
      backgroundColor: '#1b6ed9'
    }
  },
  divider: {
    margin: '1rem 0'
  },
  tabs: {
    marginBottom: '1.5rem',
    color: '#000'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 0',
    gap: '1rem'
  },
  alert: {
    marginBottom: '1rem'
  },
  treatmentsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1rem',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr'
    }
  },
  treatmentCard: {
    padding: '1rem',
    position: 'relative',
    overflow: 'auto',
    backgroundColor: '#ffffff'
  },
  hospitalCreatedCard: {
    borderLeft: '4px solid #2783F7',
    backgroundColor: '#ffffff'
  },
  treatmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.5rem'
  },
  conditionTitle: {
    fontWeight: 'bold',
    color: '#2783F7',
    fontSize: '1.1rem'
  },
  actionButtons: {
    display: 'flex',
    gap: '0.25rem'
  },
  treatmentTextColor: {
    color: '#000000'
  },
  treatmentDate: {
    marginBottom: '0.5rem',
    fontWeight: 'bold'
  },
  treatmentProvider: {
    marginBottom: '0.5rem'
  },
  medicationsLabel: {
    fontWeight: 'bold',
    marginBottom: '0.25rem'
  },
  medicationsList: {
    margin: '0 0 0.5rem 1.5rem',
    padding: 0
  },
  treatmentNotes: {
    fontStyle: 'italic',
    marginTop: '0.5rem',
    color: '#555'
  },
  providerBadge: {
    position: 'absolute',
    top: '0',
    right: '0',
    backgroundColor: '#e6f0fd',
    color: '#2783F7',
    padding: '0.25rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    borderBottomLeftRadius: '4px'
  }
});

export default TreatmentHistoryPage;
